/**
 * Demo Synchronization Service
 * Manages step navigation and route synchronisation for the interactive demo.
 * Interaction detection has been replaced by DemoSimulationService.
 */

import { getDemoEventBus } from './demoEventBus';
import { getDemoRouteGuard } from './demoRouteGuard';
import { getDemoStateManager } from './demoStateManager';
import { waitForElement } from './demoService';
import {
  SYNC_EVENTS,
  ROUTE_NAVIGATION_CONFIG,
  STATE_PRESERVATION_CONFIG,
} from '@/demo/constants';

/**
 * Demo Synchronization Service
 * Singleton service for managing demo synchronization
 */
class DemoSynchronizationService {
  constructor() {
    /**
     * Reference to the global event bus
     * @type {DemoEventBus}
     */
    this.eventBus = getDemoEventBus();

    /**
     * Reference to the route guard
     * @type {DemoRouteGuard}
     */
    this.routeGuard = getDemoRouteGuard();

    /**
     * Reference to the state manager
     * @type {DemoStateManager}
     */
    this.stateManager = getDemoStateManager();

    /**
     * Vue Router instance
     * @type {Object|null}
     */
    this.router = null;

    /**
     * Vuex store instance
     * @type {Object|null}
     */
    this.store = null;

    /**
     * Current demo steps configuration
     * @type {Array<Object>}
     */
    this.demoSteps = [];

    /**
     * Current step index
     * @type {number}
     */
    this.currentStepIndex = 0;

    /**
     * Flag indicating if synchronization is active
     * @type {boolean}
     */
    this.syncActive = false;
  }

  /**
   * Initialize synchronization with demo steps
   * @param {Array<Object>} demoSteps - Array of demo step configurations
   * @param {number} currentStepIndex - Starting step index
   * @param {Object} router - Vue Router instance (optional)
   * @param {Object} store - Vuex store instance (optional)
   * @param {boolean} resumeFromSaved - Whether to restore previous state (default: false for fresh start)
   * @returns {Promise<boolean>} - True if initialization successful
   */
  async initialize(demoSteps, currentStepIndex = 0, router = null, store = null, resumeFromSaved = false) {
    console.log('[DemoSynchronizationService] Initialize called with', demoSteps.length, 'steps, resumeFromSaved:', resumeFromSaved);
    try {
      if (!Array.isArray(demoSteps) || demoSteps.length === 0) {
        throw new Error('[DemoSynchronizationService] Invalid demo steps array');
      }

      if (currentStepIndex < 0 || currentStepIndex >= demoSteps.length) {
        throw new Error('[DemoSynchronizationService] Invalid step index');
      }

      console.log('[DemoSynchronizationService] Beginning initialization...');

      // Set router instance if provided
      if (router) {
        this.router = router;
        window.__demoRouter = router; // Store as window property for route guard access
      }

      // Set store instance if provided
      if (store) {
        this.store = store;
        window.__demoStore = store; // Store as window property for other services access
      }

      this.demoSteps = demoSteps;
      this.currentStepIndex = currentStepIndex;
      this.syncActive = true;

      // Initialize route guard if router is available
      if (ROUTE_NAVIGATION_CONFIG.AUTO_NAVIGATE_ON_STEP_CHANGE && this.router) {
        this.routeGuard.initializeGuard(this.router, demoSteps);
      }

      // Only restore state if explicitly resuming from saved state
      // For fresh demos, always start from the given currentStepIndex
      if (resumeFromSaved) {
        // Try to restore state from storage (Phase 6 integration)
        if (this.stateManager) {
          const restoredState = await this.stateManager.attemptStateRecovery();
          if (restoredState && typeof restoredState.currentStepIndex === 'number') {
            this.currentStepIndex = restoredState.currentStepIndex;
            console.log(
              `[DemoSynchronizationService] State recovered from step ${this.currentStepIndex}`
            );
          }
        }

        // Fallback to route guard restoration if state manager recovery failed
        if (STATE_PRESERVATION_CONFIG.AUTO_RESTORE_STATE && this.stateManager === null) {
          const restoredState = this.routeGuard.restoreState();
          if (restoredState && restoredState.currentStepIndex !== undefined) {
            this.currentStepIndex = restoredState.currentStepIndex;
          }
        }
      } else {
        console.log('[DemoSynchronizationService] Fresh demo start - skipping saved state recovery');
      }

      // Navigate to the initial step's page if it exists
      const initialStep = this.demoSteps[this.currentStepIndex];
      if (initialStep && initialStep.pageRoute && this.router) {
        console.log('[DemoSynchronizationService] 🌐 Navigating to initial step page:', initialStep.pageRoute);
        try {
          await this._navigateToStepRoute(initialStep);
          console.log('[DemoSynchronizationService] ✅ Navigation to initial page complete');
        } catch (error) {
          console.warn('[DemoSynchronizationService] ⚠️ Failed to navigate to initial page:', error.message);
        }
      }

      // Initialize synchronization for current step
      await this.initializeCurrentStep();

      // Record initialization in history (Phase 6)
      if (this.stateManager) {
        await this.stateManager.recordHistoryEntry('demo-initialized', {
          stepIndex: this.currentStepIndex,
          stepId: this.demoSteps[this.currentStepIndex].id,
          totalSteps: this.demoSteps.length,
        });
      }

      this.eventBus.emit(SYNC_EVENTS.SYNC_INITIALIZED, {
        stepIndex: this.currentStepIndex,
        stepId: this.demoSteps[this.currentStepIndex].id,
        totalSteps: this.demoSteps.length,
      });

      console.log('[DemoSynchronizationService] Initialization COMPLETE - ready for interactions');
      return true;
    } catch (error) {
      console.error('[DemoSynchronizationService] Initialization failed:', error);
      this.eventBus.emit(SYNC_EVENTS.SYNC_ERROR, {
        error: error.message,
        context: 'initialization',
      });
      return false;
    }
  }

  /**
   * Set Vue Router instance for multi-page navigation
   * @param {Object} router - Vue Router instance
   */
  setRouter(router) {
    this.router = router;
    window.__demoRouter = router;

    // Initialize route guard if not already done
    if (ROUTE_NAVIGATION_CONFIG.AUTO_NAVIGATE_ON_STEP_CHANGE && !this.routeGuard.isDemoActive) {
      this.routeGuard.initializeGuard(this.router, this.demoSteps);
    }
  }

  /**
   * Initialize synchronization for the current step
   * @private
   * @returns {Promise<boolean>}
   */
  async initializeCurrentStep() {
    const currentStep = this.demoSteps[this.currentStepIndex];
    if (!currentStep) {
      return false;
    }
    return true;
  }























  /**
   * Delay execution for a specified time
   * @private
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  _delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Advance to the next step with route detection and multi-page support
   * @returns {Promise<boolean>} - True if advancement successful
   */
  async advanceToNextStep() {
    try {
      console.log('[DemoSynchronizationService] ▶️ advanceToNextStep() started');
      const nextIndex = this.currentStepIndex + 1;

      if (nextIndex >= this.demoSteps.length) {
        // Demo completed
        console.log('[DemoSynchronizationService] 🎉 Demo completed (no more steps)');
        this.eventBus.emit(SYNC_EVENTS.DEMO_COMPLETED, {
          totalSteps: this.demoSteps.length,
          timestamp: Date.now(),
        });
        return false;
      }

      const currentStep = this.demoSteps[this.currentStepIndex];
      const nextStep = this.demoSteps[nextIndex];
      console.log('[DemoSynchronizationService] 📝 Next step:', nextStep.id);

      // Always navigate to the next step's route to ensure content is loaded
      // This handles both multi-page transitions and same-page steps that need content to load
      if (nextStep.pageRoute) {
        console.log(`[DemoSynchronizationService] 🌐 Navigating to route: ${nextStep.pageRoute}`);
        await this._navigateToStepRoute(nextStep);
      }

      // Update step index LOCALLY
      this.currentStepIndex = nextIndex;
      console.log('[DemoSynchronizationService] 🔄 Local step index updated to:', this.currentStepIndex);

      // UPDATE VUEX STORE SYNCHRONOUSLY to trigger UI updates immediately
      if (this.store) {
        console.log('[DemoSynchronizationService] 🔴 Dispatching demo/nextStep to Vuex store...');
        this.store.dispatch('demo/nextStep');
        console.log('[DemoSynchronizationService] 🟢 Vuex store updated');
      } else {
        console.warn('[DemoSynchronizationService] ⚠️ Vuex store not available');
      }

      // Preserve state after step advancement
      if (STATE_PRESERVATION_CONFIG.PRESERVE_ON_STEP_CHANGE && this.routeGuard) {
        this.routeGuard._preserveState();
      }

      // Save state to persistent storage (Phase 6 integration)
      if (this.stateManager) {
        await this.stateManager.saveState({
          currentStepIndex: this.currentStepIndex,
          demoSteps: this.demoSteps,
          completedSteps: Array.from({ length: nextIndex }, (_, i) => i),
          completionPercentage: Math.round(
            ((nextIndex + 1) / this.demoSteps.length) * 100
          ),
        });

        await this.stateManager.recordHistoryEntry('step-advanced', {
          fromStepIndex: this.currentStepIndex - 1,
          toStepIndex: this.currentStepIndex,
          stepId: nextStep.id,
          timestamp: Date.now(),
        });
      }

      // Emit event to notify step advancement
      this.eventBus.emit(SYNC_EVENTS.STEP_ADVANCED, {
        stepIndex: this.currentStepIndex,
        stepId: nextStep.id,
        fromRoute: currentStep.pageRoute,
        toRoute: nextStep.pageRoute,
      });

      // Initialize current step in the background (don't wait for it)
      // This allows the UI to update immediately while we're setting up listeners
      console.log('[DemoSynchronizationService] 🔨 Starting background initialization of step:', nextStep.id);
      this.initializeCurrentStep().catch(error => {
        console.error(
          `[DemoSynchronizationService] ❌ Background initialization of step "${nextStep.id}" failed:`,
          error
        );
        this.eventBus.emit(SYNC_EVENTS.SYNC_ERROR, {
          error: error.message,
          context: 'step_initialization',
          stepIndex: this.currentStepIndex,
        });
      });

      console.log('[DemoSynchronizationService] ✅ advanceToNextStep() completed successfully (initialization in background)');
      return true;
    } catch (error) {
      console.error('[DemoSynchronizationService] ❌ Failed to advance step:', error);
      this.eventBus.emit(SYNC_EVENTS.SYNC_ERROR, {
        error: error.message,
        context: 'step_advancement',
        stepIndex: this.currentStepIndex,
      });
      return false;
    }
  }

  /**
   * Go to previous step with route detection
   * @returns {Promise<boolean>} - True if navigation successful
   */
  async goToPreviousStep() {
    try {
      if (this.currentStepIndex === 0) {
        console.warn('[DemoSynchronizationService] Already at first step');
        return false;
      }

      const currentStep = this.demoSteps[this.currentStepIndex];
      const previousStep = this.demoSteps[this.currentStepIndex - 1];

      // Always navigate to the previous step's route to ensure content is loaded
      if (previousStep.pageRoute) {
        console.log(`[DemoSynchronizationService] Navigating to previous step route: ${previousStep.pageRoute}`);
        await this._navigateToStepRoute(previousStep);
      }

      // Update step index LOCALLY
      this.currentStepIndex--;

      // UPDATE VUEX STORE SYNCHRONOUSLY to trigger UI updates immediately
      if (this.store) {
        this.store.dispatch('demo/previousStep');
      }

      // Preserve state after step change
      if (STATE_PRESERVATION_CONFIG.PRESERVE_ON_STEP_CHANGE && this.routeGuard) {
        this.routeGuard._preserveState();
      }

      // Save state to persistent storage (Phase 6 integration)
      if (this.stateManager) {
        await this.stateManager.saveState({
          currentStepIndex: this.currentStepIndex,
          demoSteps: this.demoSteps,
          completedSteps: Array.from({ length: this.currentStepIndex }, (_, i) => i),
          completionPercentage: Math.round(
            ((this.currentStepIndex + 1) / this.demoSteps.length) * 100
          ),
        });

        await this.stateManager.recordHistoryEntry('step-previous', {
          fromStepIndex: this.currentStepIndex + 1,
          toStepIndex: this.currentStepIndex,
          stepId: previousStep.id,
          timestamp: Date.now(),
        });
      }

      // Emit event to notify step change
      this.eventBus.emit(SYNC_EVENTS.STEP_PREVIOUS, {
        stepIndex: this.currentStepIndex,
        stepId: previousStep.id,
        fromRoute: currentStep.pageRoute,
        toRoute: previousStep.pageRoute,
      });

      // Initialize current step in the background (don't wait for it)
      // This allows the UI to update immediately while we're setting up listeners
      this.initializeCurrentStep().catch(error => {
        console.error(
          `[DemoSynchronizationService] Background initialization of step "${previousStep.id}" failed:`,
          error
        );
        this.eventBus.emit(SYNC_EVENTS.SYNC_ERROR, {
          error: error.message,
          context: 'step_initialization',
          stepIndex: this.currentStepIndex,
        });
      });

      return true;
    } catch (error) {
      console.error('[DemoSynchronizationService] Failed to go to previous step:', error);
      this.eventBus.emit(SYNC_EVENTS.SYNC_ERROR, {
        error: error.message,
        context: 'previous_step',
        stepIndex: this.currentStepIndex,
      });
      return false;
    }
  }

  /**
   * Check if navigation involves page/route change
   * @private
   * @param {Object} currentStep - Current step
   * @param {Object} nextStep - Next step
   * @returns {boolean} - True if routes are different
   */
  _isMultiPageTransition(currentStep, nextStep) {
    if (!currentStep || !nextStep) {
      return false;
    }

    const currentRoute = currentStep.pageRoute;
    const nextRoute = nextStep.pageRoute;

    return currentRoute !== nextRoute;
  }

  /**
   * Navigate to step's route using Vue Router
   * @private
   * @param {Object} step - Target step configuration
   * @returns {Promise<void>}
   */
  async _navigateToStepRoute(step) {
    try {
      if (!step.pageRoute) {
        return;
      }

      // Check if already on correct route
      if (this.router && this.router.currentRoute.value?.path === step.pageRoute) {
        return;
      }

      // Get router instance
      const router = this.router || window.__demoRouter;

      if (!router) {
        console.warn('[DemoSynchronizationService] Router not available for navigation');
        return;
      }

      if (ROUTE_NAVIGATION_CONFIG.LOG_NAVIGATION) {
        console.log(`[DemoSynchronizationService] Navigating to route: ${step.pageRoute}`);
      }

      // Preserve state before navigation
      if (STATE_PRESERVATION_CONFIG.PRESERVE_ON_ROUTE_CHANGE) {
        this.routeGuard._preserveState();
      }

      // Navigate to the step's route
      if (ROUTE_NAVIGATION_CONFIG.NAVIGATION_METHOD === 'router-push') {
        if (ROUTE_NAVIGATION_CONFIG.REPLACE_HISTORY) {
          await router.replace(step.pageRoute);
        } else {
          await router.push(step.pageRoute);
        }
      } else {
        window.location.href = step.pageRoute;
      }

      // Wait for route navigation to complete
      await this._waitForRouteNavigation(step);
    } catch (error) {
      console.error(`[DemoSynchronizationService] Failed to navigate to route "${step.pageRoute}":`, error);
      
      // Let route guard handle the error
      if (this.routeGuard) {
        await this.routeGuard.handleNavigationError('INVALID_ROUTE_FOR_STEP', {
          route: step.pageRoute,
          step: step.id,
          error,
        });
      }

      throw error;
    }
  }

  /**
   * Wait for route navigation to complete and element to appear
   * @private
   * @param {Object} step - Target step configuration
   * @returns {Promise<void>}
   */
  async _waitForRouteNavigation(step) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 10;
      const checkInterval = 100;

      const checkElement = async () => {
        try {
          const element = await waitForElement(step.targetElement, 500);

          if (element) {
            resolve();
            return;
          }
        } catch (error) {
          // Continue trying
        }

        attempts++;
        if (attempts >= maxAttempts) {
          reject(
            new Error(
              `[DemoSynchronizationService] Element "${step.targetElement}" not found after navigation`
            )
          );
          return;
        }

        setTimeout(checkElement, checkInterval);
      };

      // Give DOM time to update after route change
      setTimeout(checkElement, 300);
    });
  }

  /**
   * Stop synchronization and cleanup all resources
   */
  stop() {
    // Stop route guard and clean up navigation protection
    if (this.routeGuard) {
      try {
        this.routeGuard.stopGuard();
      } catch (error) {
        console.warn('[DemoSynchronizationService] Error stopping route guard:', error);
      }
    }

    // Save final demo state and mark as completed (Phase 6 integration)
    if (this.stateManager) {
      try {
        // Determine completion status
        const isCompleted = this.currentStepIndex === this.demoSteps.length - 1;
        const status = isCompleted 
          ? 'completed' 
          : 'abandoned';

        this.stateManager.saveCompletionStatus(status, {
          finalStepIndex: this.currentStepIndex,
          totalSteps: this.demoSteps.length,
          stoppedAt: Date.now(),
        });

        this.stateManager.recordHistoryEntry('demo-stopped', {
          status,
          finalStepIndex: this.currentStepIndex,
          totalSteps: this.demoSteps.length,
        });
      } catch (error) {
        console.warn('[DemoSynchronizationService] Error saving final state:', error);
      }
    }

    this.syncActive = false;
    this.demoSteps = [];
    this.currentStepIndex = 0;

    this.eventBus.emit(SYNC_EVENTS.SYNC_STOPPED, {
      timestamp: Date.now(),
    });
  }

  /**
   * Get current step information
   * @returns {Object|null} - Current step or null
   */
  getCurrentStep() {
    return this.demoSteps[this.currentStepIndex] || null;
  }

  /**
   * Get demo progress
   * @returns {Object} - Progress information
   */
  getProgress() {
    return {
      currentStepIndex: this.currentStepIndex,
      totalSteps: this.demoSteps.length,
      progressPercentage:
        this.demoSteps.length > 0
          ? Math.round(((this.currentStepIndex + 1) / this.demoSteps.length) * 100)
          : 0,
      isComplete: this.currentStepIndex >= this.demoSteps.length - 1,
    };
  }

  /**
   * Check if synchronization is active
   * @returns {boolean}
   */
  isActive() {
    return this.syncActive;
  }
}

/**
 * Global singleton instance
 * @type {DemoSynchronizationService|null}
 */
let syncServiceInstance = null;

/**
 * Get or create the global DemoSynchronizationService instance
 * @returns {DemoSynchronizationService}
 */
export function getDemoSynchronizationService() {
  if (!syncServiceInstance) {
    syncServiceInstance = new DemoSynchronizationService();
  }
  return syncServiceInstance;
}

/**
 * Reset the service (mainly for testing)
 */
export function resetDemoSynchronizationService() {
  if (syncServiceInstance) {
    syncServiceInstance.stop();
  }
  syncServiceInstance = null;
}

export default DemoSynchronizationService;
